import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { z } from 'zod';

/**
 * Bidirectional sync between a typed filter object and the URL query
 * string (`?page=&size=&sort=&status=`). Powers deep-linkable + back-
 * button-friendly list pages (PLAN §3.2 / §3.3).
 *
 * The caller supplies a Zod schema that:
 *   - declares every filter key with a `.catch(default)` so a malformed
 *     / absent param degrades to a safe default rather than throwing.
 *   - is the single source of truth for the filter shape (no separate
 *     TS interface to drift).
 *
 * Multi-value params (e.g. `status=a,b,c`) are encoded comma-joined; the
 * schema is responsible for splitting via a `z.preprocess` / `.transform`.
 * This hook treats values opaquely: it reads raw string params, validates
 * through the schema, and writes back the patched subset.
 *
 * URL is the source of truth — there is no local React state mirror, so
 * back/forward navigation and external links Just Work.
 */
type ParamRecord = Record<string, string>;

const readRawParams = (params: URLSearchParams): ParamRecord => {
  const raw: ParamRecord = {};
  params.forEach((value, key) => {
    raw[key] = value;
  });
  return raw;
};

export interface UseUrlFiltersResult<TFilters> {
  /** Parsed + validated filter object (always valid — defaults applied). */
  filters: TFilters;
  /** Merge a partial patch into the URL query state (shallow). */
  setFilters: (patch: Partial<Record<keyof TFilters, string | number | string[] | null>>) => void;
  /** Drop every managed filter param from the URL. */
  reset: () => void;
}

export const useUrlFilters = <TSchema extends z.ZodType>(
  schema: TSchema,
): UseUrlFiltersResult<z.infer<TSchema>> => {
  type TFilters = z.infer<TSchema>;
  const [searchParams, setSearchParams] = useSearchParams();

  const filters = useMemo<TFilters>(() => {
    const raw = readRawParams(searchParams);
    return schema.parse(raw) as TFilters;
    // Re-parse whenever the query string changes.
  }, [schema, searchParams]);

  const setFilters = useCallback(
    (patch: Partial<Record<keyof TFilters, string | number | string[] | null>>) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          for (const [key, value] of Object.entries(patch)) {
            if (value === null || value === undefined || value === '') {
              next.delete(key);
            } else if (Array.isArray(value)) {
              if (value.length === 0) next.delete(key);
              else next.set(key, value.join(','));
            } else {
              next.set(key, String(value));
            }
          }
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const reset = useCallback(() => {
    setSearchParams(new URLSearchParams(), { replace: true });
  }, [setSearchParams]);

  return { filters, setFilters, reset };
};
